import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type { PublicOperatorDetailsResponse } from 'src/apis/organisation-service/types';
import type { OrganisationServicePublicRequest } from 'src/apis/organisation-service/use-organisation-service-public';
import { useAxios } from 'src/hooks/use-axios';

type HookOptions = {
  operatorUuid?: Uuid;
  organisationServicePublic: OrganisationServicePublicRequest;
};

/**
 * Every operator role is supported, but you cannot choose the details
 * of a specific role in case the organisation has multiple roles.
 * You'll get them in this order: TSO, SSO, LSO.
 */
export function usePublicOperatorDetails({
  operatorUuid,
  organisationServicePublic,
}: HookOptions) {
  const operator = useAxios(
    (axios, baseConfig, url: string) =>
      axios.request<PublicOperatorDetailsResponse>({
        ...baseConfig,
        url,
        method: 'GET',
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!organisationServicePublic.response || !operatorUuid) return;

    const url = urlTemplate
      .parse(
        organisationServicePublic.response.data._links.getPublicOperatorDetails
          .href
      )
      .expand({ organisationId: operatorUuid });
    operator.execute(url);
    // oxlint-disable-next-line exhaustive-deps
  }, [organisationServicePublic.response, operatorUuid]);

  return operator;
}

export type PublicOperatorDetailsRequest = ReturnType<
  typeof usePublicOperatorDetails
>;

export function useManualPublicOperatorDetails() {
  const operator = useAxios(
    (axios, baseConfig, url: string) =>
      axios.request<PublicOperatorDetailsResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: false }
  );

  return operator;
}

export type ManualPublicOperatorDetailsRequest = ReturnType<
  typeof useManualPublicOperatorDetails
>;

export function useManualPublicOperatorsDetails() {
  const operators = useAxios(
    async (axios, baseConfig, urls: string[]) => {
      const operatorsDetails = await Promise.all(
        urls.map((url) =>
          axios
            .request<PublicOperatorDetailsResponse>({
              ...baseConfig,
              url,
            })
            .then((response) => ({
              url,
              operator: response,
            }))
        )
      );
      return {
        operatorsDetails: operatorsDetails.map(
          (operator) => operator.operator.data
        ),
      };
    },
    { neededOnPageLoad: false }
  );

  return operators;
}

export type ManualPublicOperatorsDetailsRequest = ReturnType<
  typeof useManualPublicOperatorsDetails
>;
